// What a dimension's end or a connection's glued end is held to (docs/claude/dimensions.md,
// *Anchors*): a part's point, a room's corner, a polygon's or a wall's vertex. The points
// of a part an anchor can be taken from, where an anchor is now, and how everything
// numbered by an object's vertices follows when they are renumbered (`moveVertices`) —
// one rule for every holder, so a new holder of a `DimAnchor` is added to `held`, a
// new field a polygon or a wall numbers by its sides to `RingFields` (ringRenumber.ts),
// and nowhere else.
import type {
  DimAnchor,
  Drawing,
  GlassSegment,
  ProductInstance,
  Vec2,
  WallSide,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import {
  doorParts,
  mainRoomDoor,
  roomDoors,
} from './door';
import {
  drawsHeaterBlock,
  heaterGapBox,
  heaterGuard,
  heaterOuterCorners,
  instanceToWorld,
  toInstanceLocal,
} from './geometry';
import {
  isPolyRoom,
  polyBenchBands,
  polyEdges,
  polyInnerBenchBands,
  type VertexRemap,
} from './polyRoom';
import {
  renumberRing,
  type RingFields,
  ringFields,
  type RingSource,
  writeRingFields,
} from './ringRenumber';
import type { SnapKind } from './ui/core';
import type { SnapOf } from './viewPicks';
import { innerWallBand } from './wall';
import {
  bodyCorners,
  bodyOf,
  isTilted,
} from './tilt';
import type { Vec3 } from './tilt';
import { partHeight } from './heights';
import { traitsOf } from '../shared/categories';
import { roomDoor } from './roomDoor';

/** A point something can snap to, the anchor that keeps a dimension's or a
 *  connection's end on it when its object changes, and what it is a point of
 *  (the heights 3D offers it at, viewPicks.ts `SNAP_HEIGHTS`). */
export interface SnapCandidate { p: Vec2; anchor?: DimAnchor; kind?: SnapKind; of?: SnapOf }

/** The object an anchor is held to: a shape's or a wall's vertex, else a part. */
export function anchorObjectId(ref: DimAnchor): string {
  return ref.shape?.id ?? ref.wall?.id ?? ref.instId;
}

/** A heater block's real outlines in its own frame: the body, the clearance gap
 *  (per side) and — when a guard cover makes it differ from the gap — the outer
 *  edge. What it snaps BY (`instanceProbeCorners`) and what others snap TO
 *  (`instanceSnapPoints` corners, `collectSnapEdges` runs) both read these. */
export function heaterOutlines(inst: ProductInstance): Vec2[][] {
  const box = (b: { x0: number; y0: number; x1: number; y1: number }): Vec2[] =>
    [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
  const g = heaterGuard(inst);
  const gap = box(heaterGapBox(g.gap, inst.width, inst.depth));
  const outer = heaterOuterCorners(inst.width, inst.depth, g.gap, g.cover);
  const same = (a: Vec2[], b: Vec2[]): boolean => a.length === b.length && a.every((p) => b.some((q) => Math.abs(q.x - p.x) < 1e-6 && Math.abs(q.y - p.y) < 1e-6));
  const rings = [box({ x0: -inst.width / 2, y0: -inst.depth / 2, x1: inst.width / 2, y1: inst.depth / 2 }), gap];
  if (!same(outer, gap)) rings.push(outer);
  return rings;
}

/** The points of one part an end can be held to, each with its anchor (`snapped`:
 *  `anchorAt` re-finds it among these as the part changes). */
export function instanceSnapPoints(inst: ProductInstance): SnapCandidate[] {
  const pts: SnapCandidate[] = [];
  const hw = inst.width / 2, hd = inst.depth / 2;
  const own: SnapOf = inst.autoBenches ? { kind: 'room', id: inst.id } : { kind: 'part', id: inst.id };
  const pushLocal = (p: Vec2, of: SnapOf = own): void => {
    pts.push({ p: instanceToWorld(inst, p), anchor: { instId: inst.id, nx: hw ? p.x / hw : 0, ny: hd ? p.y / hd : 0, snapped: true }, of });
  };
  const pushFeat = (p: Vec2, feat: NonNullable<DimAnchor['feat']>): void => {
    pts.push({ p: instanceToWorld(inst, p), anchor: { instId: inst.id, nx: hw ? p.x / hw : 0, ny: hd ? p.y / hd : 0, feat, snapped: true }, of: own });
  };

  // Polygonal rooms: snap to the drawn (outer) corners — tagged so a dimension
  // tracks that exact vertex — plus the inner corners, door jambs and glass /
  // opening ends along their edges.
  if (isPolyRoom(inst)) {
    const edges = polyEdges(inst);
    edges.forEach((e, i) => {
      pushFeat(e.a, { kind: 'polyv', vertex: i });
      pushLocal(e.ia);
    });
    for (const door of roomDoors(inst)) {
      const rd = roomDoor(inst, door);
      if (rd) { pushLocal(rd.swing.hinge); pushLocal(rd.swing.closed); }
    }
    for (const kind of ['glass', 'opening'] as const) {
      for (const g of (kind === 'glass' ? inst.glass : inst.openings) || []) {
        const e = g.edge != null ? edges[g.edge] : undefined;
        if (!e) continue;
        const at = (d: number): Vec2 => ({ x: e.a.x + e.dir.x * d, y: e.a.y + e.dir.y * d });
        pushLocal(at(Math.min(g.start, g.end)));
        pushLocal(at(Math.max(g.start, g.end)));
      }
    }
    // Bench band corners — the walls' and the partitions' benches —, so dimensions
    // (and bench edits) snap to each bench's vertices, and 3D offers them at its seat.
    for (const band of [...polyBenchBands(inst), ...polyInnerBenchBands(inst)]) for (const p of band.poly) pushLocal(p, { kind: 'bench', id: inst.id, bench: band.id });
    return pts;
  }

  // Node objects (heated benches, drain/canalina runs): snap to their real
  // polyline vertices in world space rather than a bounding-box footprint.
  if (inst.nodes && inst.nodes.length) {
    // Tagged 'node', so the Node OSNAP toggle actually governs them and the
    // marker draws its own glyph — untagged they fell back to 'endpoint', which
    // left that toggle inert.
    for (const n of inst.nodes) pts.push({ p: { x: n.x, y: n.y }, kind: 'node', of: own });
    return pts;
  }

  // A part's body corners, each tagged with its index so a dimension on one
  // follows it through a later tilt. A tilted part offers all eight as the plan
  // draws it from above (tilt.ts `planOutline`), each saying how high above the
  // body's lowest point it stands (`rise`), so 3D offers it there and not at the
  // body's span; an upright one its underside's four.
  const corners = partCorners(inst);
  const tilted = isTilted(inst);
  corners.forEach((c, corner) => {
    if (!tilted && corner > 3) return;
    const p = toInstanceLocal(inst, c);
    pts.push({
      p: instanceToWorld(inst, p),
      anchor: { instId: inst.id, nx: hw ? p.x / hw : 0, ny: hd ? p.y / hd : 0, feat: { kind: 'corner', corner }, snapped: true },
      of: tilted ? { kind: 'part', id: inst.id, rise: Math.round(c.z * 1000) / 1000 } : own,
    });
  });
  pts.push({ p: instanceToWorld(inst, { x: 0, y: 0 }), anchor: { instId: inst.id, nx: 0, ny: 0, snapped: true }, kind: 'center', of: own });
  // A heater block also offers its air gap's corners and its outer edge's.
  if (drawsHeaterBlock(inst)) for (const ring of heaterOutlines(inst).slice(1)) for (const c of ring) pushLocal(c);

  // For saunas/steam rooms, also snap to the inner wall faces, dent corners,
  // glass-pane ends, door jambs and bench corners.
  if (inst.autoBenches) {
    const wt = wallThicknessOf(inst);
    const local: Vec2[] = [
      { x: -hw + wt, y: -hd + wt },
      { x: hw - wt, y: -hd + wt },
      { x: hw - wt, y: hd - wt },
      { x: -hw + wt, y: hd - wt },
    ];
    // Glass / opening ends snap at the outer wall line and carry a feature tag
    // (kind + wall + lo/hi side) so a dimension on that edge follows it when the
    // feature grows, merges or moves. Both also expose un-tagged inner-face ends
    // so objects (and dimensions measuring the wall depth) can snap to the inner
    // edge of the opening / glazing too.
    const edgePt = (wall: WallSide, c: number, inset = 0): Vec2 =>
      wall === 'front' ? { x: c, y: -hd + inset } : wall === 'back' ? { x: c, y: hd - inset }
        : wall === 'left' ? { x: -hw + inset, y: c } : { x: hw - inset, y: c };
    for (const kind of ['glass', 'opening'] as const) {
      for (const g of (kind === 'glass' ? inst.glass : inst.openings) || []) {
        const lo = Math.min(g.start, g.end), hi = Math.max(g.start, g.end);
        pushFeat(edgePt(g.wall, lo), { kind, wall: g.wall, edge: 'lo' });
        pushFeat(edgePt(g.wall, hi), { kind, wall: g.wall, edge: 'hi' });
        local.push(edgePt(g.wall, lo, wt), edgePt(g.wall, hi, wt)); // inner-face ends
      }
    }
    // Door jambs (front wall), tagged so a dimension follows the door as it moves.
    const rectDoor = mainRoomDoor(inst);
    const dp = rectDoor ? doorParts(inst, rectDoor) : null;
    if (dp) {
      const loJ = dp.jambL.x <= dp.jambR.x ? dp.jambL : dp.jambR;
      pushFeat(loJ, { kind: 'door', wall: 'front', edge: 'lo' });
      pushFeat(loJ === dp.jambL ? dp.jambR : dp.jambL, { kind: 'door', wall: 'front', edge: 'hi' });
      // Inner-face jamb ends, so objects snap to the door opening on the inner wall too.
      local.push({ x: dp.jambL.x, y: -hd + wt }, { x: dp.jambR.x, y: -hd + wt });
    }
    // Inner walls: their band corners (both faces, at the host wall and the tip).
    for (const iw of inst.innerWalls || []) {
      for (const corner of innerWallBand(inst, iw)) local.push(corner);
    }
    for (const p of local) pushLocal(p);
  }

  // Geometry vertices are snap targets too, emitted in the local frame and
  // rotated with the instance (via pushLocal) — so a rotated heater exposes snap
  // points along its own alignment, not the world axes. Limited to rooms and
  // heaters so a large imported DXF part doesn't flood the snap set.
  if (inst.geometry && (inst.autoBenches || traitsOf(inst.category).placedAsHeater)) {
    for (const e of inst.geometry.entities) {
      if (e.kind === 'polyline') for (const p of e.points) pushLocal(p);
      else if (e.kind === 'circle') pushLocal(e.center);
    }
  }
  return pts;
}

/** A part's eight body corners in the world (tilt.ts `bodyCorners`), z up from
 *  its lowest point. */
function partCorners(inst: ProductInstance): Vec3[] {
  return bodyCorners(bodyOf(inst, 0, { w: inst.width, d: inst.depth, h: partHeight(inst) }));
}

/** The world point of a feature-edge anchor (a room's corner; a legacy
 *  rectangular room's glass / opening / door edge), or null. */
function featureEdgeAt(inst: ProductInstance, ref: DimAnchor): Vec2 | null {
  const feat = ref.feat;
  if (!feat) return null;
  const hw = inst.width / 2, hd = inst.depth / 2;
  if (feat.kind === 'corner') {
    const c = partCorners(inst)[feat.corner];
    return c ? { x: c.x, y: c.y } : null;
  }
  if (feat.kind === 'polyv') {
    const e = polyEdges(inst)[feat.vertex];
    return e ? instanceToWorld(inst, e.a) : null;
  }
  if (feat.kind === 'door') {
    const rectDoor = mainRoomDoor(inst);
    const dp = rectDoor ? doorParts(inst, rectDoor) : null;
    if (!dp) return null;
    const lo = dp.jambL.x <= dp.jambR.x ? dp.jambL : dp.jambR;
    const hi = lo === dp.jambL ? dp.jambR : dp.jambL;
    return instanceToWorld(inst, feat.edge === 'lo' ? lo : hi);
  }
  const horiz = feat.wall === 'front' || feat.wall === 'back';
  const segs = ((feat.kind === 'glass' ? inst.glass : inst.openings) || []).filter((s) => s.wall === feat.wall);
  if (!segs.length) return null;
  // The old along-coordinate this anchor sat at (from its normalised position).
  const oldAlong = horiz ? ref.nx * hw : ref.ny * hd;
  const spanOf = (s: GlassSegment): [number, number] => [Math.min(s.start, s.end), Math.max(s.start, s.end)];
  // Prefer the segment whose span now contains the old edge (the merged feature);
  // otherwise the one whose matching edge is nearest.
  let chosen = segs.find((s) => { const [a, b] = spanOf(s); return a - 1 <= oldAlong && oldAlong <= b + 1; });
  if (!chosen) {
    let bd = Infinity;
    for (const s of segs) { const [a, b] = spanOf(s); const e = feat.edge === 'lo' ? a : b; if (Math.abs(e - oldAlong) < bd) { bd = Math.abs(e - oldAlong); chosen = s; } }
  }
  if (!chosen) return null;
  const [a, b] = spanOf(chosen);
  const c = feat.edge === 'lo' ? a : b;
  const wy = feat.wall === 'front' ? -hd : hd, wx = feat.wall === 'left' ? -hw : hw;
  return instanceToWorld(inst, horiz ? { x: c, y: wy } : { x: wx, y: c });
}

/** How far (in the footprint's normalised frame) a snapped anchor's point may have
 *  drifted and still be taken for the same point. */
const SAME_POINT = 0.2;

/** Where an anchor is now, whoever holds it — a dimension's end or a connection's:
 *  a polygon's or wall's vertex; a room's corner or feature edge; a point taken
 *  from the part's snap points (`snapped`), re-found among them so it stays on the
 *  same point when that point does not scale with the footprint (an inner corner
 *  keeps its wall's thickness); else the point kept in proportion to the
 *  footprint. Null when its object or vertex is gone. */
export function anchorAt(d: Drawing, ref: DimAnchor): Vec2 | null {
  if (ref.shape) {
    const v = (d.polygons || []).find((p) => p.id === ref.shape!.id)?.points[ref.shape.vertex];
    return v ? { x: v.x, y: v.y } : null;
  }
  if (ref.wall) {
    const v = (d.walls || []).find((w) => w.id === ref.wall!.id)?.points[ref.wall.vertex];
    return v ? { x: v.x, y: v.y } : null;
  }
  const inst = d.instances.find((i) => i.id === ref.instId);
  if (!inst) return null;
  if (ref.feat) { const fp = featureEdgeAt(inst, ref); if (fp) return fp; }
  if (ref.snapped) {
    let best: Vec2 | null = null, bestD = SAME_POINT;
    for (const cand of instanceSnapPoints(inst)) {
      if (!cand.anchor) continue;
      const dist = Math.hypot(cand.anchor.nx - ref.nx, cand.anchor.ny - ref.ny);
      if (dist < bestD) { bestD = dist; best = cand.p; }
    }
    if (best) return best;
  }
  return instanceToWorld(inst, { x: ref.nx * inst.width / 2, y: ref.ny * inst.depth / 2 });
}

/** One anchor the drawing holds, how its holder lets go of it where it is (`at`:
 *  its last point, when known), and how it moves to another object. */
interface Held { ref: DimAnchor; letGo(at: Vec2 | null): void; moveTo(id: string): void }

/** Every anchor in the drawing, holder by holder: the dimensions' two ends, the
 *  connections' glued ends. A new holder of a `DimAnchor` is added here. */
function held(d: Drawing): Held[] {
  const out: Held[] = [];
  for (const dim of d.dimensions ?? []) {
    // A dimension keeps its last point (`a` / `b`) when its anchor goes.
    if (dim.aRef) out.push({ ref: dim.aRef, letGo: () => { delete dim.aRef; }, moveTo: () => {} });
    if (dim.bRef) out.push({ ref: dim.bRef, letGo: () => { delete dim.bRef; }, moveTo: () => {} });
  }
  for (const c of d.connections ?? []) {
    for (const which of ['from', 'to'] as const) {
      const key = which === 'from' ? 'fromEnd' : 'toEnd';
      const end = c[key];
      if (!end?.ref || end.port || end.on) continue;
      // A connection's end becomes a free end where it was (its height kept).
      out.push({
        ref: end.ref,
        letGo: (at) => {
          if (!at) { delete end.ref; return; }
          c[which] = '';
          c[key] = { p: at, ...(end.z != null ? { z: end.z } : {}) };
        },
        moveTo: (id) => { c[which] = id; },
      });
    }
  }
  return out;
}

/** A part's outline as an `along` end runs on it (world space): a node object's
 *  run (open), a room's outline (closed, on its corners), else its footprint. */
export function instanceOutline(inst: ProductInstance): { pts: Vec2[]; closed: boolean } {
  if (inst.nodes && inst.nodes.length >= 2) return { pts: inst.nodes.map((n) => ({ x: n.x, y: n.y })), closed: false };
  if (inst.polygon && inst.polygon.length >= 3) return { pts: inst.polygon.map((p) => instanceToWorld(inst, p)), closed: true };
  const hw = inst.width / 2, hd = inst.depth / 2;
  return { pts: [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }].map((p) => instanceToWorld(inst, p)), closed: true };
}

/** The `along` glue for a point on an outline: the nearest segment, the fraction
 *  along it and the point itself. */
export function alongAt(pts: readonly Vec2[], closed: boolean, p: Vec2): { seg: number; t: number; p: Vec2 } | null {
  const n = closed ? pts.length : pts.length - 1;
  let best: { seg: number; t: number; p: Vec2; d: number } | null = null;
  for (let s = 0; s < n; s++) {
    const a = pts[s], b = pts[(s + 1) % pts.length];
    const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
    const t = L2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
    const q = { x: a.x + dx * t, y: a.y + dy * t };
    const dd = Math.hypot(p.x - q.x, p.y - q.y);
    if (!best || dd < best.d) best = { seg: s, t, p: q, d: dd };
  }
  return best ? { seg: best.seg, t: best.t, p: best.p } : null;
}

/** The outline object `id`'s vertices are numbered on, as a connection's `along`
 *  end runs on it (world space): a polygon's ring, a wall's / line's centre line, a
 *  room's outline or a node run. */
function vertexOutline(d: Drawing, id: string): { pts: Vec2[]; closed: boolean } | null {
  const poly = (d.polygons || []).find((p) => p.id === id);
  if (poly) return { pts: poly.points, closed: true };
  const wall = (d.walls || []).find((w) => w.id === id);
  if (wall) return { pts: wall.points, closed: false };
  const inst = d.instances.find((i) => i.id === id);
  return inst ? instanceOutline(inst) : null;
}

/** Where an old vertex went: a vertex of the same object or of another one. */
export interface VertexTo { id: string; vertex: number }
/** Where each vertex of the objects changed went (`id`: the object it was on);
 *  null: nowhere. */
export type VertexMap = (id: string, v: number) => VertexTo | null;

/** A connection's end glued along an object's outline, as it stood before its
 *  vertices changed: its glue, the object, its segment and point then, the
 *  outline's size, and how it moves to another object. */
interface AlongEnd {
  on: { seg: number; t: number };
  src: string;
  seg: number;
  t: number;
  at: Vec2;
  n: number;
  closed: boolean;
  own(id: string): void;
}

function alongEnds(d: Drawing, id: string): AlongEnd[] {
  const o = vertexOutline(d, id);
  if (!o || o.pts.length < 2) return [];
  const out: AlongEnd[] = [];
  const nSegs = o.closed ? o.pts.length : o.pts.length - 1;
  for (const c of d.connections ?? []) {
    for (const which of ['from', 'to'] as const) {
      const end = which === 'from' ? c.fromEnd : c.toEnd;
      const on = end?.on;
      if (on?.kind !== 'along' || end?.port || c[which] !== id) continue;
      const seg = Math.max(0, Math.min(nSegs - 1, on.seg)), t = Math.max(0, Math.min(1, on.t));
      const a = o.pts[seg], b = o.pts[(seg + 1) % o.pts.length];
      out.push({
        on, src: id, seg, t, at: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, n: o.pts.length, closed: o.closed,
        own: (to) => { c[which] = to; },
      });
    }
  }
  return out;
}

/** An `along` end put back on an outline after its object's vertices changed. A
 *  side whose two vertices stayed together keeps its fraction between them,
 *  wherever they went, and a vertex put in between gives it the part it is on.
 *  Otherwise — a side that lost a vertex, or whose vertices went to different
 *  objects — it goes to where it was, on the run between the nearest vertices
 *  kept, else onto the nearest of the objects they went to. Its offset is kept. */
function reglueAlong(d: Drawing, e: AlongEnd, map: VertexMap): void {
  // The vertex kept nearest `v` going `dir` (`v` itself when it stayed).
  const kept = (v: number, dir: 1 | -1): number | null => {
    for (let k = 0; k < e.n; k++) {
      const u = e.closed ? (((v + dir * k) % e.n) + e.n) % e.n : v + dir * k;
      if (u < 0 || u >= e.n) return null;
      if (map(e.src, u)) return u;
    }
    return null;
  };
  const end = e.closed ? (e.seg + 1) % e.n : e.seg + 1;
  const lo = kept(e.seg, -1), hi = kept(end, 1);
  const tl = lo == null ? null : map(e.src, lo), th = hi == null ? null : map(e.src, hi);
  let g: { id: string; seg: number; t: number } | null = null;
  const o = tl && th && tl.id === th.id && lo !== hi ? vertexOutline(d, tl.id) : null;
  if (o && tl && th && o.pts.length >= 2) {
    const n = o.pts.length, a = tl.vertex, b = th.vertex;
    const span = o.closed ? (b - a + n) % n : b - a;
    if (span >= 1 && a < n && b < n) {
      const run = Array.from({ length: span + 1 }, (_, k) => o.pts[(a + k) % n]);
      const p = lo === e.seg && hi === end
        ? { x: o.pts[a].x + (o.pts[b].x - o.pts[a].x) * e.t, y: o.pts[a].y + (o.pts[b].y - o.pts[a].y) * e.t }
        : e.at;
      const r = alongAt(run, false, p);
      if (r) g = { id: tl.id, seg: (a + r.seg) % n, t: r.t };
    }
  }
  if (!g) {
    let best = Infinity;
    for (const id of new Set([tl?.id, th?.id].filter((x): x is string => !!x))) {
      const out = vertexOutline(d, id);
      const r = out && out.pts.length >= 2 ? alongAt(out.pts, out.closed, e.at) : null;
      const dist = r ? Math.hypot(r.p.x - e.at.x, r.p.y - e.at.y) : Infinity;
      if (r && dist < best) { best = dist; g = { id, seg: r.seg, t: r.t }; }
    }
  }
  if (!g) return;
  e.on.seg = g.seg;
  e.on.t = g.t;
  if (g.id !== e.src) e.own(g.id);
}

/** A polygon's or a wall's ring in the drawing (a room's outline renumbers its
 *  own fields, `layoutWall` / vertexEdit). */
function ringIn(d: Drawing, id: string): { obj: RingFields & { points: Vec2[] }; kind: 'polygon' | 'wall'; closed: boolean } | null {
  const poly = (d.polygons || []).find((p) => p.id === id);
  if (poly) return { obj: poly, kind: 'polygon', closed: true };
  const wall = (d.walls || []).find((w) => w.id === id);
  return wall ? { obj: wall, kind: 'wall', closed: false } : null;
}

/** The rings of `ids` as they stand, to renumber what they number after a change,
 *  and every ring there was then. */
function ringsBefore(d: Drawing, ids: readonly string[]): { rings: Array<{ id: string; kind: 'polygon' | 'wall'; points: Vec2[]; fields: RingFields }>; existed: Set<string> } {
  const rings = ids.flatMap((id) => {
    const r = ringIn(d, id);
    return r ? [{ id, kind: r.kind, points: r.obj.points.map((p) => ({ x: p.x, y: p.y })), fields: ringFields(r.obj) }] : [];
  });
  return { rings, existed: new Set([...(d.polygons ?? []), ...(d.walls ?? [])].map((o) => o.id)) };
}

/** Every ring the old vertices went to gets its fields anew from the rings they
 *  came from (a polygon's from polygons, a wall's from walls); a ring the change
 *  made that no old vertex went to (a closed line's closing piece) from those of
 *  its kind, by where it lies. */
function ringsAfter(d: Drawing, was: ReturnType<typeof ringsBefore>, map: VertexMap): void {
  const into = new Map<string, RingSource[]>();
  const add = (id: string, src: RingSource): void => { (into.get(id) ?? into.set(id, []).get(id)!).push(src); };
  const onto = (id: string, v: number): number | null => { const t = map(id, v); return t ? t.vertex : null; };
  const reach = was.rings.map((r) => {
    const to = new Set<string>();
    r.points.forEach((_, v) => { const t = map(r.id, v); if (t) to.add(t.id); });
    return to;
  });
  was.rings.forEach((r, i) => {
    const alone = reach[i].size === 1 && reach[i].has(r.id);
    for (const id of reach[i]) {
      if (ringIn(d, id)?.kind !== r.kind) continue;
      add(id, { points: r.points, fields: r.fields, alone, map: (v) => (map(r.id, v)?.id === id ? onto(r.id, v) : null) });
    }
  });
  for (const o of [...(d.polygons ?? []), ...(d.walls ?? [])]) {
    if (was.existed.has(o.id) || into.has(o.id)) continue;
    const kind = ringIn(d, o.id)?.kind;
    for (const r of was.rings) if (r.kind === kind) add(o.id, { points: r.points, fields: r.fields, alone: false, map: () => null });
  }
  for (const [id, srcs] of into) {
    const r = ringIn(d, id)!;
    if (srcs.some((s) => Object.keys(s.fields).length)) writeRingFields(r.obj, renumberRing(srcs, r.obj.points, r.closed));
  }
}

/** What sits in a room held to one of its corners (`roomAnchor`), as it stood. */
function roomAnchored(d: Drawing, from: ReadonlySet<string>): ProductInstance[] {
  return d.instances.filter((i) => i.roomAnchor && from.has(i.roomAnchor.roomId));
}

/** A room-anchored part follows its corner's new number; when the corner went, it
 *  is held to the room's nearest corner from where it stands. */
function followRoomAnchors(d: Drawing, items: ProductInstance[], map: VertexMap): void {
  for (const item of items) {
    const a = item.roomAnchor!;
    const to = map(a.roomId, a.v);
    if (to?.id === a.roomId) { a.v = to.vertex; continue; }
    const room = d.instances.find((i) => i.id === a.roomId);
    const verts = room?.polygon;
    if (!room || !verts?.length) continue;
    const local = toInstanceLocal(room, { x: item.x, y: item.y });
    let best = 0, bestD = Infinity;
    verts.forEach((p, i) => { const dd = (p.x - local.x) ** 2 + (p.y - local.y) ** 2; if (dd < bestD) { bestD = dd; best = i; } });
    item.roomAnchor = { roomId: room.id, v: best, dx: local.x - verts[best].x, dy: local.y - verts[best].y };
  }
}

/**
 * Change the vertices of objects `ids` and keep everything numbered by them:
 * `change` makes the change and returns where each old vertex went — a vertex of
 * the same object, or of another one (an Explode's pieces, a Join's line, a line
 * split in two); null: nowhere. Every holder follows at once — a dimension's ends
 * and a connection's glued ends, on a vertex or along a side; a connection's end
 * moves to the object its vertex went to; a part held to a room's corner; and what
 * a polygon or a wall numbers by its own sides (`renumberRing`: locks, pinned
 * lengths, relations, doors / panes / openings), on every ring the vertices went
 * to. A room's corner (`polyv`) that goes away falls back to the anchor's stored
 * point; a polygon's or wall's vertex that goes away is let go where it was. Keep
 * `change` to the vertices' own change: a follow-up that renumbers again (a room's
 * regeneration) follows on its own.
 */
export function moveVertices(d: Drawing, ids: readonly string[], change: () => VertexMap | null): void {
  const from = new Set(ids);
  const rings = ringsBefore(d, ids);
  const anchored = roomAnchored(d, from);
  const mine = held(d).filter((h) => {
    const on = h.ref.shape ?? h.ref.wall;
    return on ? from.has(on.id) : from.has(h.ref.instId) && h.ref.feat?.kind === 'polyv';
  });
  const was = mine.map((h) => (h.ref.shape || h.ref.wall ? anchorAt(d, h.ref) : null));
  const along = ids.flatMap((id) => alongEnds(d, id));
  const map = change();
  if (!map) return;
  mine.forEach((h, i) => {
    const ref = h.ref;
    const on = ref.shape ?? ref.wall;
    if (on) {
      const to = map(on.id, on.vertex);
      const kind = !to ? null
        : (d.polygons || []).some((p) => p.id === to.id) ? 'shape'
        : (d.walls || []).some((w) => w.id === to.id) ? 'wall' : null;
      if (!to || !kind) { h.letGo(was[i]); return; }
      const src = on.id;
      delete ref.shape;
      delete ref.wall;
      ref[kind] = { id: to.id, vertex: to.vertex };
      if (to.id !== src) h.moveTo(to.id);
    } else if (ref.feat?.kind === 'polyv') {
      const to = map(ref.instId, ref.feat.vertex);
      if (!to || to.id !== ref.instId) delete ref.feat; else ref.feat = { kind: 'polyv', vertex: to.vertex };
    }
  });
  for (const e of along) reglueAlong(d, e, map);
  ringsAfter(d, rings, map);
  followRoomAnchors(d, anchored, map);
}

/**
 * Renumber object `id`'s vertices and keep every anchor held on them
 * (`moveVertices` for one object whose vertices stay its own): `change` changes
 * the vertices and returns how they were renumbered (an old index → its new one,
 * null for one that went away; null: nothing was renumbered).
 */
export function followVertices(d: Drawing, id: string, change: () => VertexRemap | null): void {
  moveVertices(d, [id], () => {
    const remap = change();
    return remap && ((_, v) => { const k = remap(v); return k == null ? null : { id, vertex: k }; });
  });
}

/** Where each of `points` went, found at the same place among `into` (within
 *  `tol` mm, the nearest): for a change that keeps the vertices where they were
 *  and only hands them to other objects (an Explode's pieces, a Join's line). */
export function samePlace(points: readonly Vec2[], into: ReadonlyArray<{ id: string; points: readonly Vec2[] }>, tol = 1e-6): (v: number) => VertexTo | null {
  return (v) => {
    const p = points[v];
    if (!p) return null;
    let best: VertexTo | null = null, bestD = tol;
    for (const o of into) {
      o.points.forEach((q, k) => {
        const dist = Math.hypot(q.x - p.x, q.y - p.y);
        if (dist <= bestD && !(best && dist === bestD)) { bestD = dist; best = { id: o.id, vertex: k }; }
      });
    }
    return best;
  };
}

/** The renumbering of `n` vertices when one is inserted after `after`. */
export function insertRemap(after: number): VertexRemap {
  return (v) => (v <= after ? v : v + 1);
}

/** The renumbering when vertex `index` is removed. */
export function removeRemap(index: number): VertexRemap {
  return (v) => (v === index ? null : v < index ? v : v - 1);
}
